import { useState, useRef } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/lib/supabase";

type MsgType = "SMS" | "LMS" | "MMS";

const MAX_LENGTH: Record<MsgType, number> = { SMS: 70, LMS: 2000, MMS: 2000 };
const PRICE: Record<MsgType, number> = { SMS: 20, LMS: 50, MMS: 100 };

interface QuickSendProps {
  onSendComplete?: () => void;
  credit?: number | null;
  perMessageRate?: number | null;
}

export default function QuickSend({ onSendComplete, credit, perMessageRate }: QuickSendProps) {
  const { user, session } = useAuth();

  const [msgType, setMsgType] = useState<MsgType>("SMS");
  const [recipient, setRecipient] = useState("");
  const [message, setMessage] = useState("");
  const [liveCount, setLiveCount] = useState(0);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const [skipSync, setSkipSync] = useState(false);
  const [localRemainingCredit, setLocalRemainingCredit] = useState<number | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync message state into textarea DOM when it changes externally (msgType switch, clear)
  if (skipSync) {
    // This runs on every render; the actual sync is in the JSX onInput handler
  }

  const handleSend = async () => {
    if (!recipient.trim() || !message.trim()) return;
    if (!user || !session) {
      setSendError("로그인이 필요합니다.");
      return;
    }

    const phoneDigits = recipient.replace(/[^0-9]/g, "");
    if (phoneDigits.length < 10 || phoneDigits.length > 11) {
      setSendError("올바른 휴대폰 번호를 입력해주세요. (예: 010-0000-0000)");
      return;
    }

    setSending(true);
    setSendError(null);
    setSent(false);

    const cost = PRICE[msgType];

    try {
      const { data: result, error: fnError } = await supabase.functions.invoke("send-sms", {
        body: {
          user_id: user.id,
          recipients: [{ phone: recipient.trim(), name: null }],
          body: message.trim(),
          cost,
          is_scheduled: false,
          type: msgType,
          has_image: false,
        },
      });

      const parseResult = result && typeof result === "object" ? result : { error: "Unknown error" };

      if (fnError) {
        throw new Error(fnError?.message || "발송 실패");
      }

      if (parseResult.error) {
        const detail = parseResult.detail ? `\n상세: ${parseResult.detail}` : "";
        throw new Error(parseResult.error + detail);
      }

      // Update local remaining credit
      if (parseResult.remaining_credit != null) {
        setLocalRemainingCredit(Number(parseResult.remaining_credit));
      }

      setSent(true);
      if (onSendComplete) onSendComplete();
      setTimeout(() => {
        setSent(false);
        setRecipient("");
        setMessage("");
      }, 2500);
    } catch (err: any) {
      console.error("[QuickSend] 발송 오류:", err);
      setSendError(err?.message || "발송 중 오류가 발생했습니다.");
    } finally {
      setSending(false);
    }
  };

  const maxLen = MAX_LENGTH[msgType];
  const price = PRICE[msgType];
  const effectiveRate = perMessageRate ?? price;
  const effectiveCredit = localRemainingCredit ?? credit ?? 0;
  const remainingCount = Math.floor(effectiveCredit / effectiveRate);

  const canSend = !!recipient.trim() && !!message.trim() && !sending && !sent;

  return (
    <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-white font-semibold text-base">빠른 발송</h3>
          <p className="text-gray-500 text-xs mt-0.5">즉시 문자 발송</p>
        </div>
        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-violet-500/30 to-pink-500/30">
          <i className="ri-send-plane-line text-violet-400 text-base"></i>
        </div>
      </div>

      {/* Type selector */}
      <div className="flex bg-white/[0.05] rounded-lg p-0.5 mb-4">
        {(["SMS", "LMS", "MMS"] as MsgType[]).map((t) => (
          <button
            key={t}
            disabled={t === "MMS"}
            onClick={() => {
              if (t === "MMS") return;
              setMsgType(t);
              setMessage("");
              setSendError(null);
            }}
            className={`flex-1 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all ${
              t === "MMS"
                ? "text-gray-600 cursor-not-allowed"
                : "cursor-pointer"
            } ${
              msgType === t && t !== "MMS"
                ? "bg-gradient-to-r from-violet-600 to-pink-600 text-white"
                : t === "MMS"
                ? "text-gray-600"
                : "text-gray-500 hover:text-gray-300"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Recipient */}
      <div className="mb-3">
        <label className="block text-gray-400 text-xs mb-1.5 font-medium">수신 번호</label>
        <div className="relative">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
            <i className="ri-smartphone-line text-gray-500 text-sm"></i>
          </div>
          <input
            type="tel"
            value={recipient}
            onChange={(e) => { setRecipient(e.target.value); setSendError(null); }}
            placeholder="010-0000-0000"
            className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg pl-9 pr-4 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 transition-all"
          />
        </div>
      </div>

      {/* Message */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-gray-400 text-xs font-medium">메시지</label>
          <span className={`text-xs ${liveCount > maxLen * 0.9 ? "text-amber-400" : "text-gray-500"}`}>
            {Math.min(liveCount, maxLen)} / {maxLen}자
          </span>
        </div>
        <textarea
          ref={textareaRef}
          defaultValue={message}
          onInput={(e) => {
            const ta = e.target as HTMLTextAreaElement;
            const raw = ta.value;
            setLiveCount(raw.length);
            setSendError(null);

            if (raw.length > maxLen) {
              ta.value = raw.slice(0, maxLen);
              setLiveCount(maxLen);
              setSkipSync(true);
              setMessage(raw.slice(0, maxLen));
              setTimeout(() => setSkipSync(false), 0);
            } else {
              setSkipSync(true);
              setMessage(raw);
              setTimeout(() => setSkipSync(false), 0);
            }
          }}
          placeholder={`메시지를 입력하세요 (최대 ${maxLen}자)`}
          rows={4}
          className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2.5 text-white text-sm placeholder-gray-600 focus:outline-none focus:border-violet-500/60 transition-all resize-none"
        />
      </div>

      {/* Error */}
      {sendError && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 bg-red-500/10 rounded-lg border border-red-500/20">
          <i className="ri-error-warning-line text-red-400 text-xs mt-0.5"></i>
          <span className="text-red-300 text-xs leading-relaxed">{sendError}</span>
        </div>
      )}

      {/* Price info */}
      <div className="flex items-center justify-between mb-4 px-3 py-2 bg-white/[0.03] rounded-lg border border-white/[0.06]">
        <span className="text-gray-400 text-xs">예상 비용</span>
        <div className="text-right">
          <span className="text-white text-sm font-semibold">₩ {price.toLocaleString()} / 건</span>
          <p className="text-violet-400 text-[10px] mt-0.5">보유: 약 {remainingCount.toLocaleString()}건 가능</p>
        </div>
      </div>

      {/* Send button */}
      <button
        onClick={handleSend}
        disabled={!canSend}
        className={`w-full py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 ${
          sent
            ? "bg-emerald-500/20 border border-emerald-500/30 text-emerald-400"
            : "bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 disabled:opacity-40 text-white"
        }`}
      >
        {sent ? (
          <><i className="ri-checkbox-circle-line text-base"></i>발송 완료!</>
        ) : sending ? (
          <><i className="ri-loader-4-line animate-spin text-base"></i>발송 중...</>
        ) : (
          <><i className="ri-send-plane-line text-base"></i>즉시 발송</>
        )}
      </button>
    </div>
  );
}